import { StringInputRules } from './StringInputRules';
import { parseInputPattern, inputFormatLabel } from './string-input-rules';
import { useAttributeGroups } from '@/hooks/useAlcoholAttributes';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, Check, LockKeyhole, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { type Attribute, TYPE_LABELS } from './attribute-model';

const schema = z
  .object({
    allowMultipleValues: z.boolean(),
    patternError: z.string(),
    inputMode: z.enum(['free', 'list']),
    allowedValues: z.array(z.string()),
    pattern: z.string().max(500, '입력 패턴은 500자 이내로 입력하세요.'),
    groupId: z.string().optional(),
    key: z
      .string()
      .trim()
      .regex(/^[a-z][a-z0-9_]{0,63}$/, '소문자로 시작하는 영문·숫자·밑줄 64자 이내로 입력하세요.'),
    displayName: z.string().trim().min(1, '속성 이름을 입력하세요.').max(100),
    type: z.enum(['KEYWORD', 'PRICE', 'POPULARITY', 'FLAG', 'IDENTITY', 'NOTE']),
    datatype: z.enum(['STRING', 'INT', 'DOUBLE', 'BOOLEAN']),
    unit: z.string().trim().max(20),
    description: z.string().trim().max(500),
  })
  .superRefine((values, ctx) => {
    if (values.datatype !== 'STRING') return;
    if (values.patternError)
      ctx.addIssue({ code: 'custom', path: ['patternError'], message: values.patternError });
    if (values.inputMode !== 'list') return;
    const items = values.allowedValues.map((value) => value.trim());
    if (!items.length || items.length > 100 || items.some((value) => !value || value.length > 255))
      ctx.addIssue({
        code: 'custom',
        path: ['allowedValues'],
        message: '선택 항목을 1개 이상 입력하세요. 항목당 255자, 최대 100개까지 추가할 수 있어요.',
      });
    if (new Set(items).size !== items.length)
      ctx.addIssue({
        code: 'custom',
        path: ['allowedValues'],
        message: '중복된 선택 항목이 있어요. 다른 값으로 바꾸거나 삭제하세요.',
      });
    // Only our fixed presets are checked locally; arbitrary Java patterns stay server-validated.
    if (
      values.pattern &&
      parseInputPattern(values.pattern) !== null &&
      items.some((value) => !new RegExp(`^(?:${values.pattern})$`).test(value))
    )
      ctx.addIssue({
        code: 'custom',
        path: ['allowedValues'],
        message: '선택 항목 중 입력 형식에 맞지 않는 값이 있어요. 항목이나 입력 형식을 바꿔주세요.',
      });
  });

type FormValues = z.infer<typeof schema>;
const selectClass =
  'h-7 w-full rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700';

export function AttributeEditor({
  attribute,
  linkedCount,
  onSave,
  onCancel,
}: {
  attribute: Attribute;
  linkedCount: number | undefined;
  onSave: (attribute: Attribute) => Promise<string | undefined>;
  onCancel: () => void;
}) {
  const groups = useAttributeGroups();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Attribute | null>(null);
  const {
    register,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      ...attribute,
      allowMultipleValues: attribute.allowMultipleValues ?? false,
      patternError: '',
      inputMode: attribute.allowedValues?.length ? 'list' : 'free',
      allowedValues: attribute.allowedValues ?? [],
      pattern: attribute.pattern ?? '',
      groupId: attribute.groupId == null ? '' : String(attribute.groupId),
    },
  });
  return (
    <form
      className="flex flex-col [&_button]:h-7 [&_button]:text-xs [&_input]:h-7 [&_input]:text-xs [&_input]:md:text-xs"
      onSubmit={handleSubmit(
        ({ inputMode, allowedValues, patternError: _patternError, ...values }) => {
          setPreview({
            ...values,
            allowedValues:
              values.datatype === 'STRING' && inputMode === 'list'
                ? allowedValues.map((value) => value.trim())
                : null,
            pattern: values.datatype === 'STRING' ? values.pattern || null : null,
            groupId: groups.isSuccess
              ? values.groupId
                ? Number(values.groupId)
                : null
              : attribute.groupId,
            id: attribute.id,
          });
        }
      )}
    >
      <div className="border-b p-3">
        <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
          <span>{attribute.id ? '속성 상세 · 편집' : '새 속성'}</span>
          <span className="font-mono">{attribute.id ? `#${attribute.id}` : 'NEW'}</span>
        </div>
        <h2 className="text-sm font-semibold tracking-tight">
          {attribute.id ? attribute.displayName : '공통 속성 만들기'}
        </h2>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {attribute.id
            ? linkedCount === undefined
              ? '연결된 위스키 수를 확인 중입니다.'
              : `현재 ${linkedCount}개 위스키가 이 속성을 공유합니다.`
            : '먼저 사전에 등록하고 필요한 위스키에 연결하세요.'}
        </p>
      </div>
      <div className="space-y-2.5 p-3">
        <label className="block space-y-1 text-xs font-medium">
          속성 이름 <span className="text-rose-600">*</span>
          <Input {...register('displayName')} placeholder="예: 선물용" maxLength={100} />
        </label>
        <label className="block space-y-1 text-xs font-medium">
          <span className="flex items-center gap-1.5">
            고유 키 {attribute.id > 0 && <LockKeyhole className="h-3 w-3" />}
          </span>
          <Input
            {...register('key')}
            readOnly={attribute.id > 0}
            placeholder="예: gift"
            className="font-mono text-xs read-only:bg-slate-50 read-only:text-slate-500"
          />
          <span className="block font-normal text-muted-foreground">
            {attribute.id
              ? '등록한 키는 변경할 수 없습니다.'
              : '영문 소문자·숫자·밑줄을 사용합니다.'}
          </span>
        </label>
        <label className="block space-y-1 text-xs font-medium">
          속성 그룹
          <select {...register('groupId')} className={selectClass} disabled={!groups.isSuccess}>
            <option value="">미분류</option>
            {attribute.groupId != null &&
              !groups.data?.some((group) => group.id === attribute.groupId) && (
                <option value={attribute.groupId}>기존 그룹 #{attribute.groupId}</option>
              )}
            {groups.data?.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
          {groups.error && (
            <span role="alert" className="text-red-700">
              그룹을 불러오지 못했습니다. 기존 그룹은 유지됩니다.
            </span>
          )}
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="space-y-1 text-xs font-medium">
            분류
            <select {...register('type')} className={selectClass}>
              {Object.entries(TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-xs font-medium">
            값 형식
            <select
              {...register('datatype', {
                onChange: () => setValue('patternError', '', { shouldValidate: true }),
              })}
              className={selectClass}
              aria-disabled={linkedCount === undefined || linkedCount > 0}
            >
              <option
                disabled={
                  (linkedCount === undefined || linkedCount > 0) && attribute.datatype !== 'STRING'
                }
                value="STRING"
              >
                문자
              </option>
              <option
                disabled={
                  (linkedCount === undefined || linkedCount > 0) && attribute.datatype !== 'INT'
                }
                value="INT"
              >
                정수
              </option>
              <option
                disabled={
                  (linkedCount === undefined || linkedCount > 0) && attribute.datatype !== 'DOUBLE'
                }
                value="DOUBLE"
              >
                소수
              </option>
              <option
                disabled={
                  (linkedCount === undefined || linkedCount > 0) && attribute.datatype !== 'BOOLEAN'
                }
                value="BOOLEAN"
              >
                참 / 거짓
              </option>
            </select>
          </label>
        </div>
        <label className="block space-y-1 text-xs font-medium">
          단위
          <Input {...register('unit')} placeholder="원, 점" maxLength={20} />
        </label>
        <p className="text-xs text-muted-foreground">
          값은 위스키를 연결할 때 입력합니다. 연결된 속성의 값 형식은 변경할 수 없습니다.
        </p>
        <label className="block space-y-1 text-xs font-medium">
          설명
          <Textarea
            {...register('description')}
            className="min-h-12 resize-y text-xs"
            placeholder="속성을 연결할 때 참고할 기준"
            maxLength={500}
          />
        </label>
        <div className="space-y-1 rounded-md border p-3">
          <label className="flex items-center gap-2 text-xs font-medium">
            <input
              type="checkbox"
              {...register('allowMultipleValues')}
              className="!h-4 w-4 shrink-0 accent-teal-700"
            />
            여러 값 연결 허용
          </label>
          <p className="text-xs leading-relaxed text-slate-500">
            한 위스키에 서로 다른 답변을 함께 연결할 수 있습니다. 같은 값은 중복 저장하지 않습니다.
          </p>
        </div>
        {attribute.allowMultipleValues && !watch('allowMultipleValues') && (
          <p className="text-xs leading-relaxed text-amber-700">
            해제하려면 각 위스키에 연결된 값을 하나만 남겨야 합니다. 여러 값이 남아 있으면 저장할 수
            없습니다.
          </p>
        )}
        {watch('datatype') === 'STRING' && (
          <StringInputRules
            mode={watch('inputMode')}
            values={watch('allowedValues')}
            pattern={watch('pattern')}
            onModeChange={(mode) => setValue('inputMode', mode, { shouldDirty: true })}
            onValuesChange={(values) =>
              setValue('allowedValues', values, { shouldDirty: true, shouldValidate: true })
            }
            onPatternError={(error) => setValue('patternError', error, { shouldValidate: true })}
            onPatternChange={(pattern) =>
              setValue('pattern', pattern, { shouldDirty: true, shouldValidate: true })
            }
          />
        )}
        {attribute.id > 0 && (linkedCount === undefined || linkedCount > 0) && (
          <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              저장하면 연결된{' '}
              <strong>
                {linkedCount === undefined ? '모든 연결 위스키' : `${linkedCount}개 위스키`}
              </strong>
              에 바뀐 의미가 적용됩니다. 기존 연결은 유지됩니다.
            </p>
          </div>
        )}
        {Object.keys(errors).length > 0 && (
          <div role="alert" className="space-y-1 text-xs text-red-700">
            {Object.entries(errors).map(([field, error]) => (
              <p key={field}>{error.message}</p>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 border-t bg-slate-50 p-3">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          편집 취소
        </Button>
        <Button
          type="submit"
          size="sm"
          className="bg-teal-800 hover:bg-teal-900"
          disabled={attribute.id > 0 && !isDirty}
        >
          {attribute.id ? <Check /> : <Plus />}
          {attribute.id ? '변경 내용 확인' : '등록 내용 확인'}
        </Button>
      </div>
      <Dialog
        open={preview !== null}
        onOpenChange={(open) => {
          if (!open && !saving) setPreview(null);
        }}
      >
        <DialogContent className="attribute-workspace max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{attribute.id ? '공통 속성 변경 확인' : '새 속성 등록 확인'}</DialogTitle>
            <DialogDescription>
              정의를 비교하고 연결된 위스키에 미치는 영향을 확인하세요.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="p-3">항목</th>
                      <th className="p-3">변경 전</th>
                      <th className="p-3">변경 후</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(
                      [
                        ['displayName', '속성 이름'],
                        ['key', '고유 키'],
                        ['type', '분류'],
                        ['datatype', '값 형식'],
                        ['allowMultipleValues', '여러 값 연결'],
                        ['unit', '단위'],
                        ['description', '설명'],
                        ['allowedValues', '선택 항목'],
                        ['pattern', '입력 형식'],
                        ['groupId', '속성 그룹'],
                      ] as const
                    ).map(([field, label]) => {
                      const format = (item: Attribute) =>
                        field === 'allowMultipleValues'
                          ? item.allowMultipleValues
                            ? '허용'
                            : '허용하지 않음'
                          : field === 'type'
                            ? TYPE_LABELS[item.type]
                            : field === 'groupId'
                              ? (groups.data?.find((group) => group.id === item.groupId)?.name ??
                                (item.groupId ? `그룹 #${item.groupId}` : '미분류'))
                              : field === 'pattern'
                                ? inputFormatLabel(item.pattern)
                                : field === 'allowedValues'
                                  ? item.allowedValues?.join('\n') || '제한 없음'
                                  : item[field] || '없음';
                      const changed = !attribute.id || attribute[field] !== preview[field];
                      return (
                        <tr key={field} className={`border-t ${changed ? 'bg-teal-50' : ''}`}>
                          <th scope="row" className="whitespace-nowrap p-3 align-top font-medium">
                            {label}
                            {changed && <span className="block text-xs text-teal-800">변경</span>}
                          </th>
                          <td className="max-w-56 whitespace-pre-wrap break-words p-3 align-top text-slate-600">
                            {attribute.id ? format(attribute) : '등록 전'}
                          </td>
                          <td className="max-w-56 whitespace-pre-wrap break-words p-3 align-top">
                            {format(preview)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
                <strong>
                  {linkedCount === undefined
                    ? '연결된 모든 위스키의'
                    : `연결된 위스키 ${linkedCount}개의`}{' '}
                  공통 정의에 반영됩니다.
                </strong>
                <p className="mt-1">
                  기존 연결은 유지됩니다. 새 정의에도 연결된 위스키가 적합한지 확인하세요. 조건에
                  따른 자동 연결은 없습니다.
                </p>
              </div>
              {saveError && (
                <p role="alert" className="text-xs text-red-700">
                  {saveError}
                </p>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={saving}
                  onClick={() => setPreview(null)}
                >
                  다시 편집
                </Button>
                <Button
                  type="button"
                  disabled={saving}
                  onClick={async () => {
                    setSaving(true);
                    setSaveError(null);
                    try {
                      const error = await onSave(preview);
                      if (error) setSaveError(error);
                      else setPreview(null);
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  {attribute.id ? '변경 저장' : '속성 등록'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </form>
  );
}
